import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useBranding } from '@/lib/branding/BrandingProvider';

export interface NeuraxInvoice {
  id: string;
  invoice_number: string;
  period_start: string;
  period_end: string;
  total_sessions: number | null;
  total_amount: number;
  currency: string;
  status: string;
  sent_at: string | null;
  paid_at: string | null;
}

export interface BillingSnapshot {
  tier: string;
  quota: { used: number; total: number; percent: number };
  invoices: NeuraxInvoice[];
}

async function fetchBilling(companyId: string): Promise<BillingSnapshot> {
  const [companyRes, invoicesRes, usageRes] = await Promise.all([
    supabase.from('b2b_companies').select('monthly_quota, tier').eq('id', companyId).single(),
    supabase
      .from('b2b_invoices')
      .select(
        'id, invoice_number, period_start, period_end, total_sessions, total_amount, currency, status, sent_at, paid_at'
      )
      .eq('b2b_company_id', companyId)
      .order('period_end', { ascending: false }),
    // Billing is per confirmed interview/assessment SETUP (one new prompt
    // generation each). The meter counts this month's billable, non-waived
    // setups — the same unit the monthly statement itemizes.
    supabase.rpc('get_b2b_monthly_usage' as never, { p_company_id: companyId } as never),
  ]);

  if (companyRes.error || !companyRes.data) {
    throw new Error(`b2b_companies: ${companyRes.error?.message ?? 'not found'}`);
  }
  if (invoicesRes.error) {
    throw new Error(`b2b_invoices: ${invoicesRes.error.message}`);
  }
  if (usageRes.error) {
    throw new Error(`get_b2b_monthly_usage: ${usageRes.error.message}`);
  }

  const total = companyRes.data.monthly_quota ?? 0;
  const used = (usageRes.data as number | null) ?? 0;
  const percent = total > 0 ? Math.round((used / total) * 100) : 0;

  return {
    tier: companyRes.data.tier ?? 'standard',
    quota: { used, total, percent },
    invoices: (invoicesRes.data ?? []) as NeuraxInvoice[],
  };
}

export function useBilling() {
  const { companyId } = useBranding();
  return useQuery({
    queryKey: ['neurax', 'billing', companyId],
    queryFn: () => fetchBilling(companyId),
    staleTime: 60_000,
  });
}
